<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <h3 class="font-weight-bold">Reward Settings</h3>
        </v-card-title>
        <v-card-subtitle>
          One set of rules per company — drives wallet limits, W Points on
          top-up, and Refer &amp; Earn rewards.
        </v-card-subtitle>

        <v-card-text>
          <section v-if="isLoading && !isLoaded" style="height: 400px">
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
          </section>

          <template v-else>
            <!-- ------------------------------------------------ Wallet -->
            <h4 class="font-weight-bold primary--text mb-2">Wallet</h4>
            <v-row>
              <v-col sm="3">
                <v-switch
                  class="mt-1"
                  hide-details="auto"
                  v-model="form.wallet_enabled"
                  label="Wallet enabled"
                  hint="Turn off to block all top-ups and wallet payments"
                  persistent-hint
                ></v-switch>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.min_topup_amount"
                  outlined
                  type="number"
                  label="Minimum top-up (₹)"
                  :disabled="!form.wallet_enabled"
                  :error-messages="errors.min_topup_amount"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.max_topup_amount"
                  outlined
                  type="number"
                  label="Maximum top-up (₹)"
                  :disabled="!form.wallet_enabled"
                  :error-messages="errors.max_topup_amount"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.max_wallet_balance"
                  outlined
                  type="number"
                  label="Maximum wallet balance (₹)"
                  hint="Leave blank for no cap"
                  persistent-hint
                  clearable
                  :disabled="!form.wallet_enabled"
                  :error-messages="errors.max_wallet_balance"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <!-- --------------------------------- Points on top-up -->
            <h4 class="font-weight-bold primary--text mb-2">
              W Points on top-up
            </h4>
            <v-row>
              <v-col sm="3">
                <v-switch
                  class="mt-1"
                  hide-details="auto"
                  v-model="form.topup_points_enabled"
                  label="Give points on top-up"
                ></v-switch>
              </v-col>
              <v-col sm="3">
                <v-select
                  v-model="form.topup_points_type"
                  outlined
                  :items="valueTypes"
                  label="Reward type"
                  :disabled="!form.topup_points_enabled"
                  :error-messages="errors.topup_points_type"
                ></v-select>
              </v-col>
              <v-col sm="2">
                <v-text-field
                  v-model="form.topup_points_value"
                  outlined
                  type="number"
                  :label="
                    form.topup_points_type === 'percent'
                      ? 'Points (% of top-up)'
                      : 'Points (flat)'
                  "
                  :disabled="!form.topup_points_enabled"
                  :error-messages="errors.topup_points_value"
                ></v-text-field>
              </v-col>
              <v-col sm="2">
                <v-text-field
                  v-model="form.topup_points_min_amount"
                  outlined
                  type="number"
                  label="Min top-up to earn (₹)"
                  :disabled="!form.topup_points_enabled"
                  :error-messages="errors.topup_points_min_amount"
                ></v-text-field>
              </v-col>
              <v-col sm="2">
                <v-text-field
                  v-model="form.topup_points_max"
                  outlined
                  type="number"
                  label="Max points per top-up"
                  hint="Blank = no cap"
                  persistent-hint
                  clearable
                  :disabled="!form.topup_points_enabled"
                  :error-messages="errors.topup_points_max"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <!-- ------------------------------------------ Refer & Earn -->
            <h4 class="font-weight-bold primary--text mb-2">Refer &amp; Earn</h4>
            <v-row>
              <v-col sm="3">
                <v-switch
                  class="mt-1"
                  hide-details="auto"
                  v-model="form.referral_enabled"
                  label="Refer &amp; Earn enabled"
                ></v-switch>
              </v-col>
              <v-col sm="3">
                <v-select
                  v-model="form.referral_reward_on"
                  outlined
                  :items="rewardOnItems"
                  label="Reward the referrer on"
                  :disabled="!form.referral_enabled"
                  :error-messages="errors.referral_reward_on"
                ></v-select>
              </v-col>
              <v-col sm="3">
                <v-select
                  v-model="form.referral_reward_type"
                  outlined
                  :items="rewardTypes"
                  label="Pay the referrer in"
                  :disabled="!form.referral_enabled"
                  :error-messages="errors.referral_reward_type"
                ></v-select>
              </v-col>
              <v-col sm="3">
                <v-select
                  v-model="form.referral_value_type"
                  outlined
                  :items="valueTypes"
                  label="Referrer reward type"
                  :disabled="!form.referral_enabled"
                  :error-messages="errors.referral_value_type"
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col sm="3">
                <v-text-field
                  v-model="form.referral_value"
                  outlined
                  type="number"
                  :label="
                    form.referral_value_type === 'percent'
                      ? 'Referrer reward (% of subscription)'
                      : 'Referrer reward (flat)'
                  "
                  :disabled="!form.referral_enabled"
                  :error-messages="errors.referral_value"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.referral_max_value"
                  outlined
                  type="number"
                  label="Cap on referrer reward"
                  hint="Blank = no cap"
                  persistent-hint
                  clearable
                  :disabled="!form.referral_enabled"
                  :error-messages="errors.referral_max_value"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.referee_value"
                  outlined
                  type="number"
                  label="Reward for the new user"
                  hint="What the person using the code gets"
                  persistent-hint
                  :disabled="!form.referral_enabled"
                  :error-messages="errors.referee_value"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <!-- ------------------------------------ Points behaviour -->
            <h4 class="font-weight-bold primary--text mb-2">
              Points behaviour
            </h4>
            <v-row>
              <v-col sm="3">
                <v-text-field
                  v-model="form.points_expiry_days"
                  outlined
                  type="number"
                  label="Points validity (days)"
                  :error-messages="errors.points_expiry_days"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.point_to_rupee_ratio"
                  outlined
                  type="number"
                  label="1 W Point = ₹"
                  hint="Used when points are redeemed"
                  persistent-hint
                  :error-messages="errors.point_to_rupee_ratio"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <!-- ------------------------------------ Booking -->
            <h4 class="font-weight-bold primary--text mb-2">Booking</h4>
            <v-row>
              <v-col sm="3">
                <v-text-field
                  v-model="form.booking_advance_days"
                  outlined
                  type="number"
                  label="Booking advance days"
                  hint="Minimum days ahead a customer must book (0 = same-day allowed)"
                  persistent-hint
                  :error-messages="errors.booking_advance_days"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <v-btn v-can:edit="'reward_settings'"
              rounded
              color="primary"
              :loading="isSaving"
              :disabled="isSaving"
              @click="save"
            >
              Update Reward Settings
            </v-btn>
          </template>
        </v-card-text>
      </v-card>

      <v-snackbar
        v-model="savedSnackbar"
        :timeout="3000"
        color="success"
        rounded="pill"
      >
        Reward settings updated.
        <template v-slot:action="{ attrs }">
          <v-btn v-can:edit="'reward_settings'" text v-bind="attrs" @click="savedSnackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "RewardSettingsPage",
  data() {
    return {
      // Defaults mirror the reward_settings migration, so the form is never
      // half-empty while the GET is still in flight
      form: {
        wallet_enabled: true,
        min_topup_amount: 100,
        max_topup_amount: 50000,
        max_wallet_balance: null,

        topup_points_enabled: true,
        topup_points_type: "percent",
        topup_points_value: 5,
        topup_points_min_amount: 0,
        topup_points_max: null,

        referral_enabled: true,
        referral_reward_on: "first_subscription",
        referral_reward_type: "points",
        referral_value_type: "fixed",
        referral_value: 100,
        referral_max_value: null,
        referee_value: 0,

        points_expiry_days: 60,
        point_to_rupee_ratio: 1,

        booking_advance_days: 3,
      },
      old_json: null,
      valueTypes: [
        { text: "Percent", value: "percent" },
        { text: "Fixed", value: "fixed" },
      ],
      rewardTypes: [
        { text: "W Points", value: "points" },
        { text: "Wallet money", value: "wallet" },
      ],
      rewardOnItems: [
        { text: "Signup", value: "signup" },
        { text: "First subscription", value: "first_subscription" },
        { text: "Every subscription", value: "every_subscription" },
      ],
      isLoading: false,
      isLoaded: false,
      isSaving: false,
      savedSnackbar: false,
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      await axios
        .get(`reward_settings`)
        .then((response) => {
          // The API creates the row with defaults on first read
          this.form = response.data.data;
          this.old_json = { ...response.data.data };
          this.isLoaded = true;
        })
        .catch((error) => {
          console.error(error);
          this.isUnAuthorised();
        });
      this.isLoading = false;
    },
    async save() {
      this.isSaving = true;
      await axios
        .post(`reward_settings/update`, this.form)
        .then(async (response) => {
          this.form = response.data.data;
          await this.saveUserTimestamp(this.old_json, this.form);
          this.old_json = { ...this.form };
          this.savedSnackbar = true;
        })
        .catch((error) => {
          console.error(error);
          this.isUnAuthorised();
        });
      this.isSaving = false;
    },
  },
};
</script>
